/**
 * SAM-1298 (W2–W4) — «Register your business»: the request body, what blocks «Send request» and the words for the
 * sign-up refusals. Pure, so it is unit-tested; RegisterBusiness.tsx renders it.
 */
import type { BusinessRequest } from '../../api/businessRequestsApi';
import type { BusinessCategory } from '../../hooks/useBusinessCrud';
import {
  EMPTY_NEW_BUSINESS, newBusinessIssues, newBusinessPayload, type NewBusinessForm, type NewBusinessIssue,
} from '../BusinessPage/newBusiness';

/** The Estate category (key `estate`): the business is the office of an estate group (EG = negocio Estate, 1–2 Nov). */
export const isEstateCategory = (categories: BusinessCategory[], id: number | null): boolean =>
  id != null && categories.find((c) => c.id === id)?.key === 'estate';

export type RegisterIssue = NewBusinessIssue | 'estate_group';

export function registerIssues(form: NewBusinessForm, estate: boolean, administersGroupId: number | null): RegisterIssue[] {
  const issues: RegisterIssue[] = newBusinessIssues(form);
  if (estate && administersGroupId == null) issues.push('estate_group');
  return issues;
}

/**
 * The `business` keys POST /sam/business-requests accepts for a new business (contract, 2 Nov, plus the SAM-1237
 * location columns agreed the same day). Others are a 400.
 */
const BUSINESS_KEYS = [
  'name', 'business_category_id', 'business_type_id', 'email', 'phone_number', 'logo_url',
  'location_type', 'building_id', 'estate_id', 'address', 'city', 'zip_code', 'country',
] as const;

/**
 * POST /sam/business-requests for a new business: the New business fields and its location (the server decides
 * is_active / show_in_app). `estate_group_id` is the group of an internal location, or for an Estate business the
 * group it asks to administer («Which estate group?»); External and Virtual have none. `owner_name` is the name
 * typed when creating the account (Requested by), when there is one.
 */
export function businessRequestBody(form: NewBusinessForm, opts: { estate: boolean; administersGroupId: number | null; ownerName?: string }) {
  const all = newBusinessPayload(form) as Record<string, unknown>;
  const business: Record<string, unknown> = {};
  for (const k of BUSINESS_KEYS) if (all[k] !== undefined && all[k] !== null && all[k] !== '') business[k] = all[k];
  if (typeof business.country === 'string') business.country = business.country.toUpperCase();
  if (opts.estate) {
    // An Estate business sits inside the group it administers (the server forces `internal`): no address, and a
    // building or unit only when they belong to that group.
    for (const k of ['address', 'city', 'zip_code', 'country']) delete business[k];
    business.location_type = 'internal';
    if (form.location.type !== 'internal' || form.location.groupId !== opts.administersGroupId) {
      delete business.building_id;
      delete business.estate_id;
    }
  }
  const groupId = opts.estate ? opts.administersGroupId : form.location.type === 'internal' ? form.location.groupId : null;
  if (groupId != null) business.estate_group_id = groupId;
  if (opts.ownerName?.trim()) business.owner_name = opts.ownerName.trim();
  return { type: 'business' as const, kind: 'new' as const, business };
}

/** A denied request's payload back into the form (identity and category; the location is picked again). */
export function formFromPayload(p: Record<string, unknown> | null | undefined): NewBusinessForm {
  const str = (v: unknown) => (typeof v === 'string' ? v : '');
  const num = (v: unknown) => (typeof v === 'number' ? v : null);
  return {
    ...EMPTY_NEW_BUSINESS,
    name: str(p?.name),
    email: str(p?.email),
    phone_number: str(p?.phone_number),
    logo_url: str(p?.logo_url),
    business_category_id: num(p?.business_category_id),
    business_type_id: num(p?.business_type_id),
  };
}

/** The owner's latest Business request (GET /sam/business-requests?mine=1), newest first. */
export const latestBusinessRequest = (list: BusinessRequest[]): BusinessRequest | null =>
  [...list].filter((r) => r.type === 'business')
    .sort((a, b) => (b.sent_at || '').localeCompare(a.sent_at || ''))[0] ?? null;

/** Where an owner with no business goes: the form, or the request already sent. */
export const ownerStep = (latest: BusinessRequest | null): 'business' | 'sent' =>
  latest && latest.status !== 'withdrawn' ? 'sent' : 'business';

/** Cognito's default policy, checked before the request so the owner reads it in the form's words. */
export const PASSWORD_RULE = 'At least 8 characters, with upper and lower case letters and a number.';
export const passwordOk = (pw: string): boolean => pw.length >= 8 && /[a-z]/.test(pw) && /[A-Z]/.test(pw) && /\d/.test(pw);

export function signUpErrorText(code: string | null, fallback: string): string {
  switch (code) {
    case 'USER_EXISTS': return 'This email already has an account. Log in with it, or use another email.';
    case 'INVALID_CODE': return 'That code is not right. Check the email and try again.';
    case 'EXPIRED_CODE': return 'That code expired. Send a new one.';
    default: return fallback;
  }
}

/** POST /sam/business-requests refusals in the form's words (contract error codes). */
export function sendErrorText(error: unknown): string {
  const data = (error as { response?: { data?: { code?: string; error?: string } } } | null)?.response?.data;
  switch (data?.code) {
    case 'estate_group_has_admin': return 'That estate group already has an administrator. Pick another one.';
    case 'resident_account': return 'This account is a resident. Create another account for the business.';
    case 'open_request_exists': return 'You already sent a request. Wait for the answer.';
    case 'already_has_business': return 'This account already has a business. Log in again to open it.';
    case 'email_not_verified': return 'Verify your email first.';
    case 'missing_field':
    case 'invalid_value': return data.error || 'Check the form and try again.';
    default: return data?.error || 'The request could not be sent. Try again.';
  }
}
